import { API_BASE } from "../api";
import { get as storeGet, put as storePut, compositeKey } from "../store";

export type CourseSettings = {
  morning_hour: number;
  morning_minute: number;
  nudge_start_hour: number;
  nudge_end_hour: number;
  evening_hour: number;
  evening_minute: number;
  active: boolean;
  duration_days: number;
};

// Экспортирован для потребителей (DashboardScreen) — легитимное "нет своей
// строки" через контрактную ветку "empty" (404/400/403/401, см. ниже) не
// несёт данных, потребитель подставляет те же DEFAULTS, что модуль сам
// использует для успешного пустого data[].
export const DEFAULTS: CourseSettings = {
  morning_hour: 6,
  morning_minute: 0,
  nudge_start_hour: 11,
  nudge_end_hour: 13,
  evening_hour: 20,
  evening_minute: 0,
  active: true,
  duration_days: 0,
};

const TTL_MS = 10 * 60 * 1000;

function cacheKey(courseId: string) {
  return `course_settings_cache_${courseId}`;
}
function readCache(courseId: string): CourseSettings | null {
  try {
    const raw = localStorage.getItem(cacheKey(courseId));
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    if (Date.now() - at > TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}
function writeCache(courseId: string, data: CourseSettings) {
  try {
    localStorage.setItem(
      cacheKey(courseId),
      JSON.stringify({ at: Date.now(), data })
    );
  } catch {}
}

// SILENT-FIX (08.09.2026): раньше отказ сети/сервера подставлял DEFAULTS
// (duration_days:0) и кэшировал их на TTL_MS как подтверждённые настройки —
// «не смогли спросить» было неотличимо от «у курса нет своих настроек»
// (см. reports/2026-09/SILENT-ERRORS-1789160733-report.md, находка I).
// Легитимное «у курса нет строки course_settings» (успешный ответ, пустой
// data[]) — по-прежнему даёт DEFAULTS и кэшируется: это настоящее «пусто»,
// не отказ. Эта логика не менялась — M5-STORE4 расширяет форму контракта.
//
// M5-STORE4 (18.09.2026): было — отказ отдавал null, недифференцированно.
// Форма — как у courses.ts/artifacts.ts (ok/empty/unavailable/cached).
// "empty" — 404/400/403/401: сервер ответил определённо ("настроек нет"/
// доступ закрыт), не отказ спросить — в IndexedDB не пишется и не читается,
// как и раньше (SILENT-FIX) в короткий кэш не пишется. "unavailable"/
// "cached" — не удалось спросить вовсе (сеть, неожиданный !res.ok, включая
// парсинг 200-ответа — тот же единый try/catch, что был у SILENT-FIX): перед
// терминальным отказом спрашиваем IndexedDB (content-access, инвариант 9).
export type CourseSettingsResult =
  | { status: "ok"; data: CourseSettings }
  | { status: "empty" }
  | { status: "unavailable" }
  | { status: "cached"; data: CourseSettings; cachedAt: number; stale: boolean };

/** Спросить IndexedDB, когда сеть не ответила. Пусто в IndexedDB → unavailable. */
async function indexedDbOrUnavailable(key: string): Promise<CourseSettingsResult> {
  const cached = await storeGet<CourseSettings>(key);
  if (cached.status === "empty") {
    console.error("[settings] no IndexedDB record, unavailable");
    return { status: "unavailable" };
  }
  console.error(`[settings] served from IndexedDB, age=${Date.now() - cached.cachedAt}`);
  return {
    status: "cached",
    data: cached.data,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

export async function getCourseSettingsByCourseId(
  courseId: string
): Promise<CourseSettingsResult> {
  // Короткий кэш (localStorage, 10 минут) — первым, как и раньше. Не
  // трогается: ни TTL, ни ключ, ни запись — своя задача, снимает повторные
  // запросы в пределах сессии, а не переживает офлайн.
  const cached = readCache(courseId);
  if (cached) return { status: "ok", data: cached };

  // IndexedDB — третий слой, только если сеть не ответила (ниже).
  const key = compositeKey("course_settings", courseId);

  try {
    const url =
      `${API_BASE}/items/course_settings` +
      `?filter[course][_eq]=${encodeURIComponent(courseId)}&limit=1`;
    const res = await fetch(url);

    if (res.status === 404 || res.status === 400 || res.status === 403 || res.status === 401) {
      return { status: "empty" };
    }

    if (!res.ok) {
      console.error("course_settings fetch error:", res.status, res.statusText);
      return indexedDbOrUnavailable(key);
    }

    const json = await res.json();
    const s = json?.data?.[0];

    const data: CourseSettings = {
      morning_hour: s?.morning_hour ?? DEFAULTS.morning_hour,
      morning_minute: s?.morning_minute ?? DEFAULTS.morning_minute,
      nudge_start_hour: s?.nudge_start_hour ?? DEFAULTS.nudge_start_hour,
      nudge_end_hour: s?.nudge_end_hour ?? DEFAULTS.nudge_end_hour,
      evening_hour: s?.evening_hour ?? DEFAULTS.evening_hour,
      evening_minute: s?.evening_minute ?? DEFAULTS.evening_minute,
      active: s?.active ?? DEFAULTS.active,
      duration_days: s?.duration_days ?? DEFAULTS.duration_days,
    };

    writeCache(courseId, data);
    await storePut(key, data, 0);
    return { status: "ok", data };
  } catch (e) {
    console.error("course_settings fetch exception:", e);
    return indexedDbOrUnavailable(key);
  }
}
